// src/pages/MasterBarang.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  Input,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Space,
  Button,
  Modal,
  Form,
  Upload,
  Switch,
  message,
} from "antd";
import {
  FileExcelOutlined,
  PlusOutlined,
  CameraOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "react-medium-image-zoom/dist/styles.css"; // styling safe to keep
import imageCompression from "browser-image-compression";
import { motion, AnimatePresence } from "framer-motion";
import "./MasterBarang.css";
import { getMasterBarang, getGroupBarang, updateBarang, getMerkList, getKategoriDokterList, getKategoriBarang, addMaster } from "../api/api";
import { io } from "socket.io-client";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;


const API_BASE =
  process.env.REACT_APP_API_BASE ||
  window.location.origin;


/* -------------------------
   PinchZoomImage component (unchanged)
   ------------------------- */
function PinchZoomImage({
  src,
  alt,
  fallbackBase64 = null,
  maxScale = 4,
  minScale = 1,
  initialScale = 1,
  className,
  imgStyle = {},
}) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [scale, setScale] = useState(initialScale);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const lastPan = useRef({ x: 0, y: 0 });
  const lastTouch = useRef({});
  const isPointerDown = useRef(false);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const distance = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
  const centerPoint = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

  const reset = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    lastPan.current = { x: 0, y: 0 };
  };

  const clampTranslate = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxOffsetX = Math.max(0, (scale - 1) * rect.width / 2 + 20);
    const maxOffsetY = Math.max(0, (scale - 1) * rect.height / 2 + 20);
    setTranslate((t) => ({ x: clamp(t.x, -maxOffsetX, maxOffsetX), y: clamp(t.y, -maxOffsetY, maxOffsetY) }));
  };

  const handleTouchStart = (e) => {
    if (!e.touches) return;
    if (e.touches.length === 2) {
      const d = distance(e.touches[0], e.touches[1]);
      const c = centerPoint(e.touches[0], e.touches[1]);
      lastTouch.current = { mode: "pinch", startDist: d, startScale: scale, center: c };
    } else if (e.touches.length === 1 && scale > 1) {
      lastTouch.current = {
        mode: "pan",
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        origX: translate.x,
        origY: translate.y,
      };
    } else {
      lastTouch.current = {};
    }
  };

  const handleTouchMove = (e) => {
    if (!e.touches) return;
    if (lastTouch.current.mode === "pinch" && e.touches.length >= 2) {
      const d = distance(e.touches[0], e.touches[1]);
      const scaleDelta = d / lastTouch.current.startDist;
      const newScale = clamp(lastTouch.current.startScale * scaleDelta, minScale, maxScale);

      const c = centerPoint(e.touches[0], e.touches[1]);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const offsetX = c.x - rect.left - rect.width / 2;
        const offsetY = c.y - rect.top - rect.height / 2;
        const factor = (newScale / scale) - 1;
        setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
      }
      setScale(newScale);
    } else if (lastTouch.current.mode === "pan" && e.touches.length === 1) {
      const dx = e.touches[0].clientX - lastTouch.current.startX;
      const dy = e.touches[0].clientY - lastTouch.current.startY;
      setTranslate({ x: lastTouch.current.origX + dx, y: lastTouch.current.origY + dy });
    }
  };

  const handleTouchEnd = () => {
    lastTouch.current = {};
    clampTranslate();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const zoomFactor = delta > 0 ? 1.08 : 0.92;
    const rect = containerRef.current?.getBoundingClientRect();
    const pointer = { x: e.clientX, y: e.clientY };
    if (rect) {
      const offsetX = pointer.x - rect.left - rect.width / 2;
      const offsetY = pointer.y - rect.top - rect.height / 2;
      const newScale = clamp(scale * zoomFactor, minScale, maxScale);
      const factor = (newScale / scale) - 1;
      setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
      setScale(newScale);
    } else {
      setScale((s) => clamp(s * zoomFactor, minScale, maxScale));
    }
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    isPointerDown.current = true;
    lastPan.current = { x: e.clientX, y: e.clientY, origX: translate.x, origY: translate.y };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isPointerDown.current) return;
    const dx = e.clientX - lastPan.current.x;
    const dy = e.clientY - lastPan.current.y;
    setTranslate({ x: lastPan.current.origX + dx, y: lastPan.current.origY + dy });
  };

  const handleMouseUp = () => {
    isPointerDown.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    clampTranslate();
  };

  const handleDoubleClick = (e) => {
    if (scale === 1) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const offsetX = e.clientX - rect.left - rect.width / 2;
        const offsetY = e.clientY - rect.top - rect.height / 2;
        const targetScale = Math.min(2, maxScale);
        const factor = (targetScale / scale) - 1;
        setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
        setScale(targetScale);
      } else {
        setScale(2);
      }
    } else {
      reset();
    }
  };

  const handleImgError = (e) => {
    try {
      const el = e.currentTarget;
      if (fallbackBase64 && el.src !== `data:image/jpeg;base64,${fallbackBase64}`) {
        el.onerror = null;
        el.src = `data:image/jpeg;base64,${fallbackBase64}`;
      } else {
        el.onerror = null;
        el.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
      }
    } catch (err) {/* silent */ }
  };

  const zoomIn = (step = 0.25) => setScale((s) => clamp(s + step, minScale, maxScale));
  const zoomOut = (step = 0.25) => setScale((s) => clamp(s - step, minScale, maxScale));

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    try {
      if (containerRef.current) {
        containerRef.current._pz = { zoomIn, zoomOut, reset, getState: () => ({ scale, translate }) };
      }
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, translate]);


  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
        touchAction: "none",
      }}
      className={className}
    >
      <img
        ref={imgRef}
        src={src || ""}
        alt={alt || ""}
        onError={handleImgError}
        draggable={false}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          display: "block",
          transformOrigin: "center center",
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: "transform 0.02s linear",
          willChange: "transform",
          ...imgStyle,
        }}
      />
      <div style={{ display: "none" }} data-zoom-state={JSON.stringify({ scale, translate })} />
    </div>
  );
}

/* -------------------------
   Main component: MasterBarang
   ------------------------- */
const MasterBarang = ({ propAccess = [] }) => {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(() => localStorage.getItem("selectedGroup") || null);
  const [selectedCategory, setSelectedCategory] = useState(() => localStorage.getItem("selectedCategory") || null);
  const [selectedMerk, setSelectedMerk] = useState(null);
  const [selectedKategoriDokter, setSelectedKategoriDokter] = useState(null);
  const [barangList, setBarangList] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("searchTerm") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [lastUpdatedKode, setLastUpdatedKode] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [merkList, setMerkList] = useState([]);
  const [kategoriDokterList, setKategoriDokterList] = useState([]);
  const [addModal, setAddModal] = useState(null);
  const [addForm] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState("AKTIF");
  const socketRef = useRef(null);
  const selectedGroupRef = useRef(selectedGroup);
  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);
  useEffect(() => {

    socketRef.current = io("https://srv.omdcgroup.com", {
      path: "/socket.io/",
      transports: ["polling"]
    });


    socketRef.current.on("connect", () => {
      console.log("✅ SOCKET CONNECTED:", socketRef.current.id);
    });

    socketRef.current.on("connect_error", err => {
      console.log("❌ SOCKET ERROR:", err.message);
    });

    socketRef.current.on("disconnect", () => {
      console.log("⚠ SOCKET DISCONNECT");
    });
    socketRef.current.on("group_added", (data) => {

      setGroups(prev => {
        if (prev.includes(data.grup_brg)) return prev;
        return [...prev, data.grup_brg].sort();
      });

    });


    socketRef.current.on("barang_updated", (data) => {

      setBarangList(prev => {

        const isSameGroup = data.grup_brg === selectedGroupRef.current;
        const exists = prev.some(b => b.kd_brg === data.kd_brg);

        // ===============================
        // 1️⃣ Barang pindah keluar grup
        // ===============================
        if (!isSameGroup && exists) {
          return prev.filter(b => b.kd_brg !== data.kd_brg);
        }

        // ===============================
        // 2️⃣ Barang pindah MASUK ke grup ini
        // ===============================
        if (isSameGroup && !exists) {

          // fetch ulang 1 barang biar lengkap (termasuk gambar)
          getMasterBarang(selectedGroupRef.current).then(res => {
            if (res.status === "ok") {
              const found = res.data.find(b => b.kd_brg === data.kd_brg);
              if (found) {
                setBarangList(current => [
                  {
                    ...found,
                    gambarBase64: found.gambar || null,
                    imageUrl: `${API_BASE}/images/${found.kd_brg}?_=${Date.now()}`,
                    _rt: Date.now()
                  },
                  ...current
                ]);
              }
            }
          });

          return prev;
        }

        // ===============================
        // 3️⃣ Barang tetap di grup ini → update biasa
        // ===============================
        if (isSameGroup && exists) {

          getMasterBarang(selectedGroupRef.current).then(res => {
            if (res.status === "ok") {

              const refreshed = res.data.map(b => ({
                ...b,
                gambarBase64: b.gambar || null,
                imageUrl: `${API_BASE}/images/${b.kd_brg}?_=${Date.now()}`,
                _rt: Date.now()
              }));

              setBarangList(refreshed);
            }
          });

          return prev; // sementara biarkan dulu
        }

        return prev;
      });

    });


    return () => socketRef.current.disconnect();

  }, []);

  const openAddModal = (type) => {
    addForm.resetFields(); // reset input modal tambah
    setAddModal(type);
  };




  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();
  const usersession = user?.name || "SYSTEM";
  const [userAccess] = useState(
    propAccess.length > 0 ? propAccess : Array.isArray(user?.access) ? user.access.map(Number) : []
  );

  const getBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const cacheBustedUrl = (url) => {
    if (!url) return url;

    // 🚀 jangan sentuh base64
    if (url.startsWith("data:")) return url;

    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}_=${Date.now()}`;
  };


  const resolveImageSrc = (b) => {
    if (!b) return null;

    // prioritas base64 kalau ada
    if (b.gambarBase64)
      return `data:image/jpeg;base64,${b.gambarBase64}`;

    // pakai URL dari loadBarang
    return b.imageUrl || null;
  };





  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getGroupBarang();
        if (res.status === "ok" && Array.isArray(res.data)) setGroups(res.data);
        else setError(res.message || "Data grup tidak valid");
      } catch (err) {
        console.error("Error fetch groups:", err);
        setError("Gagal mengambil data grup");
      }
    };
    fetchGroups();
  }, []);

  // loadBarang: prefer base64 if server returned it; otherwise build image URL
  const loadBarang = React.useCallback(async (group, status) => {
    const res = await getMasterBarang(group, status);

    if (res.status === "ok") {
      const barang = res.data.map(b => ({
        ...b,
        gambarBase64: b.gambar || null,
        imageUrl: `${API_BASE}/images/${b.kd_brg}`
      }));

      setBarangList(barang);
    } else {
      setBarangList([]);
    }
  }, []);

  // LOAD BARANG: dipanggil setiap kali selectedGroup berubah
  useEffect(() => {
    if (!selectedGroup) {
      setBarangList([]);
      return;
    }

    setBarangList([]);
    setLoading(true);

    let mounted = true;

    (async () => {
      try {
        await loadBarang(selectedGroup, selectedStatus);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => (mounted = false);

  }, [selectedGroup, selectedStatus, loadBarang]);



  useEffect(() => {
    const loadDropdown = async () => {

      const merkRes = await getMerkList();
      console.log("MERK:", merkRes);

      const dokRes = await getKategoriDokterList();


      const katRes = await getKategoriBarang();


      if (merkRes.status === "ok")
        setMerkList(merkRes.data);

      if (dokRes.status === "ok")
        setKategoriDokterList(dokRes.data);

      if (katRes.status === "ok")
        setCategories(katRes.data);
    };

    loadDropdown();
  }, []);



  useEffect(() => {
    selectedGroup ? localStorage.setItem("selectedGroup", selectedGroup) : localStorage.removeItem("selectedGroup");
  }, [selectedGroup, selectedStatus]);

  useEffect(() => {
    selectedCategory ? localStorage.setItem("selectedCategory", selectedCategory) : localStorage.removeItem("selectedCategory");
  }, [selectedCategory]);

  useEffect(() => {
    localStorage.setItem("searchTerm", searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const container = document.querySelector(".master-container");
    if (!container) return;
    let lastY = 0;
    let ticking = false;
    const handleScroll = () => {
      const currentY = container.scrollTop;
      setIsScrolled(currentY > 5);
      setShowScrollTop(currentY > 250);
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (currentY > lastY + 10 && currentY > 100) setShowFilter(false);
          else if (currentY < lastY - 10) setShowFilter(true);
          lastY = currentY <= 0 ? 0 : currentY;
          ticking = false;
        });
        ticking = true;
      }
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!lastUpdatedKode) return;
    const el = document.getElementById(`barang-${lastUpdatedKode}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("highlight-card");
    const timeoutId = setTimeout(() => {
      el.classList.remove("highlight-card");
      setLastUpdatedKode(null);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [barangList, lastUpdatedKode]);

  const filteredBarang = barangList.filter((b) => {

    const matchSearch =
      b.nm_barang?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.kd_brg?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchCategory =
      selectedCategory ? b.kategory_brg === selectedCategory : true;

    const matchMerk =
      selectedMerk ? b.flag_3 === selectedMerk : true;

    const matchKategoriDokter =
      selectedKategoriDokter ? b.flag_4 === selectedKategoriDokter : true;

    const matchStatus =
      selectedStatus ? b.sts_brg === selectedStatus : true;

    return (
      matchSearch &&
      matchCategory &&
      matchMerk &&
      matchKategoriDokter &&
      matchStatus
    );
  });


  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("MASTER BARANG");
      ws.columns = [
        { header: "Kode Barang", key: "kd_brg", width: 20 },
        { header: "Nama Barang", key: "nm_barang", width: 35 },
        { header: "Merk", key: "flag_3", width: 20 },
        { header: "Grup", key: "grup_brg", width: 20 },
        { header: "Kategori", key: "kategory_brg", width: 20 },
        { header: "Kategori Dokter", key: "flag_4", width: 20 },
        { header: "Fungsi/Tindakan", key: "val_3", width: 50 },
        { header: "Status", key: "sts_brg", width: 15 },
        { header: "Gambar", key: "gambar", width: 20 },
      ];
      for (let b of filteredBarang) {
        const rowNumber = ws.addRow({
          kd_brg: b.kd_brg,
          nm_barang: b.nm_barang,
          flag_3: b.flag_3,
          grup_brg: b.grup_brg,
          kategory_brg: b.kategory_brg,
          flag_4: b.flag_4,
          val_3: b.val_3,
          sts_brg: b.sts_brg,
        }).number;
        ws.getRow(rowNumber).height = 60;
        ws.getRow(rowNumber).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
        const base64Str = b.gambarBase64 || b.gambar || null;
        if (base64Str) {
          const base64Data = base64Str.replace(/^data:image\/\w+;base64,/, "");
          const imageBuffer = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
          const imageId = workbook.addImage({ buffer: imageBuffer, extension: "jpeg" });
          ws.addImage(imageId, { tl: { col: 8, row: rowNumber - 1 }, ext: { width: 160, height: 105 } });
        }
      }
      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `MasterBarang_${selectedGroup || "All"}.xlsx`);
      message.success("Export Excel berhasil ✅");
    } catch (error) {
      console.error("❌ Error export Excel:", error);
      message.error("Gagal mengekspor data ke Excel");
    } finally {
      setExporting(false);
    }
  };

  const openForm = (barang) => {
    if (!userAccess.includes(1128)) {
      message.warning("Anda tidak memiliki akses ❌");
      return;
    }

    const normalize = (v) => (v || "").toString().trim();

    const merk = normalize(barang.flag_3);
    const kategori = normalize(barang.kategory_brg);
    const dokter = normalize(barang.flag_4);
    const grup = normalize(barang.grup_brg);

    setGroups(prev =>
      grup && !prev.includes(grup) ? [...prev, grup] : prev
    );

    // inject dropdown
    setMerkList(prev =>
      merk && !prev.includes(merk) ? [...prev, merk] : prev
    );

    setCategories(prev =>
      kategori && !prev.includes(kategori) ? [...prev, kategori] : prev
    );

    setKategoriDokterList(prev =>
      dokter && !prev.includes(dokter) ? [...prev, dokter] : prev
    );

    // isi form
    form.setFieldsValue({
      kd_brg: barang.kd_brg,
      nm_barang: barang.nm_barang,
      grup_brg: grup,
      merk,
      kategory_brg: kategori,
      kategori_dokter: dokter,
      val_3: barang.val_3,
      status: barang.sts_brg === "AKTIF",
    });

    const previewSrc =
      // ✅ prioritaskan base64 (stabil, tidak reload)
      (barang.gambarBase64
        ? `data:image/jpeg;base64,${barang.gambarBase64}`
        : null)

      // ✅ fallback ke URL server
      || (barang.imageUrl &&
        (barang.imageUrl.startsWith("http")
          ? barang.imageUrl
          : `${API_BASE}${barang.imageUrl}`));

    setPhotoPreview(previewSrc);
    setFormVisible(true);

  };




  const handleFormSubmit = async (values) => {
    if (!values.kd_brg || !values.nm_barang) return;

    setSaving(true);

    try {
      let gambarFile = null;

      // ✅ compress + convert to file
      if (photoPreview && photoPreview.startsWith("data:image")) {

        const originalBlob = await fetch(photoPreview).then(r => r.blob());

        const compressedBlob = await imageCompression(originalBlob, {
          maxSizeMB: 0.05,
          useWebWorker: true,
        });

        gambarFile = new File(
          [compressedBlob],
          "image.jpg",
          { type: compressedBlob.type }
        );
      }

      // ✅ payload
      const payload = {
        kd_brg: values.kd_brg,
        nm_barang: values.nm_barang,
        grup_brg: values.grup_brg,
        kategory_brg: values.kategory_brg,
        flag_3: values.merk || "",
        flag_4: values.kategori_dokter || "",
        val_3: values.val_3 || "",
        sts_brg: values.status ? "AKTIF" : "TIDAK AKTIF",
        val_2: usersession,
        gambar: gambarFile,
      };

      console.log("UPLOAD PAYLOAD:", payload);

      const res = await updateBarang(payload);

      if (res.status === "ok") {

        message.success("Barang berhasil diperbarui ✅");

        setFormVisible(false);

      }
      else {
        message.error(res.message || "Gagal update barang ❌");
      }

    } catch (err) {

      console.error("❌ Submit error:", err);
      message.error("Terjadi kesalahan saat menyimpan");

    } finally {

      setSaving(false);

    }
  };


  const openPreview = (barang) => {
    const rawApi = barang.imageUrl
      ? (barang.imageUrl.startsWith("http") ? barang.imageUrl.replace(/size=thumb/, "size=full") : `${API_BASE}${barang.imageUrl.replace(/size=thumb/, "size=full")}`)
      : `${API_BASE}/images/${encodeURIComponent(barang.kd_brg)}?size=full&format=webp`;

    const apiImageUrl = cacheBustedUrl(rawApi);
    const gambarBase64 = barang.gambarBase64 || barang.gambar || null;
    const fullImageUrl = apiImageUrl || (gambarBase64 ? `data:image/jpeg;base64,${gambarBase64}` : null);

    setPreviewData({ ...barang, fullImageUrl, gambarBase64 });
    setPreviewVisible(true);
    previewContainerRef.current = null;
  };

  const openCamera = () => {
    if (!/Android|iPhone|iPad/i.test(navigator.userAgent)) {
      message.info("Fitur kamera hanya tersedia di perangkat mobile 📱");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleCameraChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await getBase64(file);
      const base64Only = dataUrl.split(",")[1];
      setPhotoPreview(dataUrl);
      form.setFieldsValue({ gambar: [{ originFileObj: file }] });

      const kdBrg = form.getFieldValue("kd_brg");
      setBarangList((prev) => prev.map((b) =>
        b.kd_brg === kdBrg ? { ...b, gambarBase64: base64Only, gambar: base64Only, imageUrl: dataUrl } : b
      ));
    } catch (err) {
      console.error("Error camera change:", err);
      message.error("Gagal membaca file gambar.");
    }
  };

  const callPreviewZoom = (cmd) => {
    try {
      const stored = previewContainerRef.current;
      if (stored && stored._pz) {
        if (cmd === "in") stored._pz.zoomIn();
        else if (cmd === "out") stored._pz.zoomOut();
        else if (cmd === "reset") stored._pz.reset();
        return;
      }
      const c = document.querySelector(".ant-modal-root .ant-modal-content .pinch-zoom-container");
      if (c && c._pz) {
        if (cmd === "in") c._pz.zoomIn();
        else if (cmd === "out") c._pz.zoomOut();
        else if (cmd === "reset") c._pz.reset();
      }
    } catch (e) { /* fail silently */ }
  };

  const scrollToTop = () => {
    const container = document.querySelector(".master-container");
    if (container) container.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="master-container fade-in">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleCameraChange}
      />
      <Title level={3}>MASTER BARANG</Title>

      <div className={`filter-bar ${isScrolled ? "scrolled" : ""} ${showFilter ? "show" : "hide"}`}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Pilih Grup Barang"
              value={selectedGroup}
              onChange={(val) => {
                // reset UI quickly, then load via effect
                setSelectedCategory(null);
                setSelectedMerk(null);
                setSelectedKategoriDokter(null);
                setSearchTerm("");
                setBarangList([]); // clear old items while new group loads
                setSelectedGroup(val);
              }}
              style={{ width: "100%" }}
              allowClear
            >
              {groups.map((g) => (
                <Option key={g} value={g}>{g}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Merk"
              value={selectedMerk}
              onChange={setSelectedMerk}
              allowClear
              style={{ width: "100%" }}
            >
              {merkList.map((m) => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Kategori"
              value={selectedCategory}
              onChange={setSelectedCategory}
              allowClear
              disabled={!categories.length}
              style={{ width: "100%" }}
            >
              {categories.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Kategori Dokter"
              value={selectedKategoriDokter}
              onChange={setSelectedKategoriDokter}
              allowClear
              style={{ width: "100%" }}
            >
              {kategoriDokterList.map((k) => (
                <Option key={k} value={k}>{k}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Input.Search
              placeholder="Cari kode/nama barang..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              disabled={!selectedGroup}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status Barang"
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: "100%" }}
            >
              <Option value="AKTIF">🟢 AKTIF</Option>
              <Option value="TIDAK AKTIF">🔴 TIDAK AKTIF</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                disabled={filteredBarang.length === 0}
              >
                Export Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {loading && (
        <div className="center"><Spin size="large" /></div>
      )}

      {error && !loading && <Alert type="error" message="Error" description={error} showIcon />}

      {!loading && !selectedGroup && <Empty description="Pilih grup dahulu" />}
      {!loading && selectedGroup && filteredBarang.length === 0 && <Empty description="Tidak ada data barang" />}

      {selectedGroup && (
        <Row gutter={[16, 16]} justify="start" className="barang-grid fade-in">
          {filteredBarang.length > 0 ? filteredBarang.map((b) => {
            const imgSrc = resolveImageSrc(b);
            return (
              <Col id={`barang-${b.kd_brg}`} xs={12} sm={8} md={6} lg={4} key={b.kd_brg + (b._rt || "")}
              >
                <Card
                  hoverable
                  onClick={() => openForm(b)}
                  className="barang-card"
                  cover={
                    <div className="barang-cover" onClick={(e) => { e.stopPropagation(); openPreview(b); }}>
                      {imgSrc ? (
                        <img
                          key={b.kd_brg + "-" + (b._rt || 0)}
                          src={imgSrc}
                          alt={b.nm_barang || "Barang"}
                          style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 6 }}
                          onError={(e) => {
                            try {
                              const fb = b.gambarBase64 ? `data:image/jpeg;base64,${b.gambarBase64}` : null;
                              if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
                              else if (!e.currentTarget.src || !e.currentTarget.src.startsWith("data:")) e.currentTarget.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
                            } catch (err) { /* silent */ }
                          }}
                          loading="eager"
                        />
                      ) : <div className="no-image">No Image</div>}
                    </div>
                  }
                >
                  <Card.Meta
                    title={<div className="asset-title">{b.nm_barang || "-"}</div>}
                    description={
                      <div className="asset-details">
                        <div><strong>Kode:</strong> {b.kd_brg || "-"}</div>

                        {/* MERK */}
                        <div><strong>Merk:</strong> {b.flag_3 || "-"}</div>

                        <div><strong>Grup:</strong> {b.grup_brg || "-"}</div>
                        <div><strong>Kategori:</strong> {b.kategory_brg || "-"}</div>

                        {/* KATEGORI DOKTER */}
                        <div><strong>Kategori Dokter:</strong> {b.flag_4 || "-"}</div>

                        <div className={`status ${b.sts_brg === "AKTIF" ? "aktif" : "nonaktif"}`}>
                          {b.sts_brg || "-"}
                        </div>
                      </div>
                    }
                  />

                </Card>
              </Col>
            );
          }) : <Col span={24}><Empty description="Tidak ada data barang" /></Col>}
        </Row>
      )}

      {/* Modal Update */}
      <Modal
        open={formVisible}
        title="Update Barang"
        onCancel={() => setFormVisible(false)}
        onOk={() => form.submit()}
        okText="Simpan"
        destroyOnHidden
        centered
        width={window.innerWidth < 768 ? "95%" : 600}
        styles={{ body: { maxHeight: window.innerWidth < 768 ? "70vh" : "60vh", overflowY: "auto", padding: "1rem" } }}
        style={{ top: window.innerWidth < 768 ? 10 : 50 }}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item label="Kode Barang" name="kd_brg"><Input disabled /></Form.Item>
          <Form.Item label="Nama Barang" name="nm_barang" rules={[{ required: true, message: "Nama barang wajib diisi" }]}><Input /></Form.Item>
          <Form.Item label="Grup Barang">
            <Space.Compact style={{ width: "100%" }}>

              <Form.Item name="grup_brg" noStyle>
                <Select showSearch style={{ width: "100%" }}>
                  {groups.map(g => (
                    <Option key={g} value={g}>{g}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Button icon={<PlusOutlined />} onClick={() => openAddModal("grup")} />

            </Space.Compact>
          </Form.Item>

          <Form.Item label="Merk">
            <Space.Compact style={{ width: "100%" }}>

              <Form.Item name="merk" noStyle>
                <Select showSearch style={{ width: "100%" }}>
                  {merkList.map(m => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Button icon={<PlusOutlined />} onClick={() => openAddModal("merk")} />

            </Space.Compact>
          </Form.Item>


          <Form.Item label="Kategori Barang">
            <Space.Compact style={{ width: "100%" }}>

              <Form.Item name="kategory_brg" noStyle>
                <Select showSearch style={{ width: "100%" }}>
                  {categories.map(c => (
                    <Option key={c} value={c}>{c}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Button icon={<PlusOutlined />} onClick={() => openAddModal("kategori")} />

            </Space.Compact>
          </Form.Item>



          <Form.Item label="Kategori Dokter">
            <Space.Compact style={{ width: "100%" }}>

              <Form.Item name="kategori_dokter" noStyle>
                <Select showSearch style={{ width: "100%" }}>
                  {kategoriDokterList.map(k => (
                    <Option key={k} value={k}>{k}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Button icon={<PlusOutlined />} onClick={() => openAddModal("dokter")} />

            </Space.Compact>
          </Form.Item>


          <Form.Item label="Fungsi/Tindakan" name="val_3"><TextArea rows={window.innerWidth < 768 ? 3 : 4} /></Form.Item>
          <Form.Item label="Status" name="status" valuePropName="checked"><Switch checkedChildren="AKTIF" unCheckedChildren="TIDAK AKTIF" /></Form.Item>

          <Form.Item label="Gambar">

            {/* 🔥 tombol sejajar */}
            <div style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap"
            }}>

              <Form.Item
                name="gambar"
                valuePropName="fileList"
                getValueFromEvent={(e) => e && e.fileList}
                noStyle
              >
                <Upload
                  listType="picture"
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={() => false}
                  accept="image/*"
                  onChange={async (info) => {
                    const file = info.fileList[0]?.originFileObj;
                    if (file) {
                      try {
                        const dataUrl = await getBase64(file);
                        const base64Only = dataUrl.split(",")[1];
                        setPhotoPreview(dataUrl);

                        const kdBrg = form.getFieldValue("kd_brg");

                        setBarangList(prev =>
                          prev.map(b =>
                            b.kd_brg === kdBrg
                              ? { ...b, gambarBase64: base64Only, gambar: base64Only, imageUrl: dataUrl }
                              : b
                          )
                        );

                      } catch (err) {
                        message.error("Gagal membaca file gambar");
                      }
                    } else {
                      setPhotoPreview(null);
                    }
                  }}
                >
                  <Button icon={<PlusOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>

              <Button icon={<CameraOutlined />} onClick={openCamera}>
                Kamera
              </Button>

            </div>

            {/* preview */}
            <div style={{ marginTop: 10 }}>
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Preview"
                  style={{
                    width: 160,
                    borderRadius: 8,
                    objectFit: "contain",
                    maxHeight: 120
                  }}
                />
              ) : (
                <div style={{
                  color: "#aaa",
                  border: "1px dashed #ccc",
                  borderRadius: 8,
                  padding: 8
                }}>
                  Tidak ada gambar
                </div>
              )}
            </div>

          </Form.Item>

        </Form>
      </Modal>


      {/* Modal Preview */}
      <Modal
        open={previewVisible}
        title={previewData?.nm_barang || "Preview"}
        onCancel={() => setPreviewVisible(false)}
        footer={<Button onClick={() => setPreviewVisible(false)}>Tutup</Button>}
        centered
        width={window.innerWidth < 768 ? "90%" : 600}
        styles={{ body: { padding: "1rem", maxHeight: "80vh", overflowY: "auto" } }}
        style={{ top: window.innerWidth < 768 ? 10 : 50 }}
      >
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 30, display: "flex", gap: 8 }}>
            <Button size="small" onClick={() => callPreviewZoom("out")}>−</Button>
            <Button size="small" onClick={() => callPreviewZoom("in")}>+</Button>
            <Button size="small" onClick={() => callPreviewZoom("reset")}>Reset</Button>
          </div>

          <div style={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {previewData?.fullImageUrl || previewData?.gambarBase64 ? (
              <div className="pinch-zoom-container" ref={(el) => { if (el) previewContainerRef.current = el; }}>
                <PinchZoomImage
                  src={previewData?.fullImageUrl || (previewData?.gambarBase64 ? `data:image/jpeg;base64,${previewData.gambarBase64}` : "")}
                  alt={previewData?.nm_barang || "Preview"}
                  fallbackBase64={previewData?.gambarBase64}
                  maxScale={4}
                  minScale={1}
                  imgStyle={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 480 }}
                />
              </div>
            ) : <Empty description="Tidak ada gambar" />}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <strong>Fungsi/Tindakan:</strong>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{previewData?.val_3 || "-"}</div>
        </div>
      </Modal>

      {/* Modal Tambah Master kategori,merk,kategori dokter */}
      <Modal
        open={!!addModal}
        title={`Tambah ${addModal}`}
        onCancel={() => setAddModal(null)}
        footer={null}
        zIndex={2000}
        maskClosable={false}
        centered
      >

        <Form
          form={addForm}
          onFinish={async ({ value }) => {

            const res = await addMaster(addModal, value, usersession);

            if (res.status === "exist") {
              message.warning("Maaf data sudah ada");
              return;
            }

            if (res.status === "ok") {

              message.success("Berhasil ditambahkan");
              if (addModal === "grup") {
                const g = await getGroupBarang();
                setGroups(g.data);
              }

              if (addModal === "merk") {
                const m = await getMerkList();
                setMerkList(m.data);
              }

              if (addModal === "kategori") {
                const k = await getKategoriBarang();
                setCategories(k.data);
              }

              if (addModal === "dokter") {
                const d = await getKategoriDokterList();
                setKategoriDokterList(d.data);
              }

              setAddModal(null);
            }
          }}
        >
          <Form.Item
            name="value"
            rules={[{ required: true, message: "Wajib diisi" }]}
          >
            <Input placeholder={`Nama ${addModal}`} />
          </Form.Item>

          <Button type="primary" htmlType="submit">
            Simpan
          </Button>
        </Form>
      </Modal>


      {saving && (<div className="overlay-loading"><Spin size="large" tip="Menyimpan data..." /></div>)}

      <AnimatePresence>{showScrollTop && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.25 }}
          onClick={scrollToTop}
          style={{ position: "fixed", bottom: 30, right: 30, background: "#1890ff", color: "#fff", borderRadius: "50%", padding: 12, cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.3)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          whileHover={{ scale: 1.08 }}>
          <ArrowUpOutlined style={{ fontSize: 18 }} />
        </motion.div>
      )}</AnimatePresence>

      {exporting && (<div className="overlay-loading"><Spin size="large" tip="Mengekspor ke Excel..." /></div>)}
    </div>
  );
};

export default MasterBarang;